/**
 * US federal holidays — V98-0-4 (PRD G-6). Data as code, rule-driven so it
 * covers any year (tested 2025–2030) without a yearly table to maintain.
 *
 * Convention (stated once, cited by every business-day deadline):
 *   - The eleven legal public holidays of 5 U.S.C. 6103(a). Juneteenth
 *     counts from 2021 (Pub. L. 117-17).
 *   - Observed-date shift per 5 U.S.C. 6103(b) / EO 11582: a holiday on a
 *     Saturday is observed the Friday before, on a Sunday the Monday after.
 *     New Year's Day on a Saturday is therefore observed on Dec 31 of the
 *     previous year (e.g. Fri 2027-12-31 for 2028-01-01).
 *   - Only the observed date is a non-business day. The actual date, when it
 *     falls on a weekend, is already skipped as a weekend.
 *   - NOT included: Inauguration Day (5 U.S.C. 6103(c) — DC-area employees
 *     only), ad-hoc executive-order closures (e.g. Christmas Eve), and state
 *     or agency-local holidays.
 *   - Dates are calendar dates in UTC, matching the rest of the timeframe
 *     arithmetic (certification-service addTimeframe).
 */
/** Cited on every business-day deadline so the calendar is auditable. */
export const US_FEDERAL_HOLIDAY_CALENDAR = 'us-federal-5usc6103@2021';
// month is 1-based; weekday 0 = Sunday … 6 = Saturday.
export const US_FEDERAL_HOLIDAY_RULES = [
    { id: 'new_years_day', name: "New Year's Day", kind: 'fixed', month: 1, day: 1 },
    { id: 'mlk_day', name: 'Birthday of Martin Luther King, Jr.', kind: 'nth_weekday', month: 1, weekday: 1, nth: 3 },
    { id: 'washingtons_birthday', name: "Washington's Birthday", kind: 'nth_weekday', month: 2, weekday: 1, nth: 3 },
    { id: 'memorial_day', name: 'Memorial Day', kind: 'nth_weekday', month: 5, weekday: 1, nth: 'last' },
    { id: 'juneteenth', name: 'Juneteenth National Independence Day', kind: 'fixed', month: 6, day: 19, from_year: 2021 },
    { id: 'independence_day', name: 'Independence Day', kind: 'fixed', month: 7, day: 4 },
    { id: 'labor_day', name: 'Labor Day', kind: 'nth_weekday', month: 9, weekday: 1, nth: 1 },
    { id: 'columbus_day', name: 'Columbus Day', kind: 'nth_weekday', month: 10, weekday: 1, nth: 2 },
    { id: 'veterans_day', name: 'Veterans Day', kind: 'fixed', month: 11, day: 11 },
    { id: 'thanksgiving_day', name: 'Thanksgiving Day', kind: 'nth_weekday', month: 11, weekday: 4, nth: 4 },
    { id: 'christmas_day', name: 'Christmas Day', kind: 'fixed', month: 12, day: 25 },
];
const iso = (d) => d.toISOString().slice(0, 10);
function actualDate(rule, year) {
    if (rule.kind === 'fixed')
        return new Date(Date.UTC(year, rule.month - 1, rule.day));
    if (rule.nth === 'last') {
        const d = new Date(Date.UTC(year, rule.month, 0)); // last day of the month
        d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() - rule.weekday + 7) % 7));
        return d;
    }
    const d = new Date(Date.UTC(year, rule.month - 1, 1));
    d.setUTCDate(1 + ((rule.weekday - d.getUTCDay() + 7) % 7) + (rule.nth - 1) * 7);
    return d;
}
function observedDate(actual) {
    const d = new Date(actual.getTime());
    if (d.getUTCDay() === 6)
        d.setUTCDate(d.getUTCDate() - 1);
    else if (d.getUTCDay() === 0)
        d.setUTCDate(d.getUTCDate() + 1);
    return d;
}
const byYear = new Map();
/** Holidays *observed* in `year`, in date order (includes a Dec 31 shift from next year's New Year's Day). */
export function usFederalHolidays(year) {
    const cached = byYear.get(year);
    if (cached)
        return cached;
    const out = [];
    for (const y of [year, year + 1]) {
        for (const rule of US_FEDERAL_HOLIDAY_RULES) {
            if (rule.from_year && y < rule.from_year)
                continue;
            const actual = actualDate(rule, y);
            const observed = observedDate(actual);
            if (observed.getUTCFullYear() === year)
                out.push({ id: rule.id, name: rule.name, observed: iso(observed), actual: iso(actual) });
        }
    }
    out.sort((a, b) => a.observed.localeCompare(b.observed));
    byYear.set(year, out);
    return out;
}
/** True when the UTC calendar date of `d` is an observed US federal holiday. */
export function isUsFederalHoliday(d) {
    const day = iso(d);
    return usFederalHolidays(d.getUTCFullYear()).some((h) => h.observed === day);
}
//# sourceMappingURL=us-federal-holidays.js.map